import React, { useState } from "react";
import { handleFileUpload } from "../../scripts/S3";
import ImageCropModal from "../ImageCropModal";
import GoogleReviewFinderModal from "./GoogleReviewFinderModal";
import { Upload, Camera, Search } from "lucide-react";

const Question = ({ question, value, onChange, answers }) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [googleReviewFinderOpen, setGoogleReviewFinderOpen] = useState(false);

  // Check if this is the Google Review Link question
  const isGoogleReviewQuestion = question.id === "googleReviewLink";
  if (!question) {
    return <div>Loading question...</div>; // Or handle the undefined state appropriately
  }

  function addSpacesToPhoneNumber(phoneNumber) {
    // remove all spaces from the string
    phoneNumber = phoneNumber.replace(/\s/g, "");

    let newPhoneNum = "";

    for (let i = 0; i < Math.min(phoneNumber.length, 10); i++) {
      if (i === 3 || i === 6) {
        newPhoneNum += " ";
      }
      newPhoneNum += phoneNumber[i];
    }

    return newPhoneNum;
  }

  const handleInputChange = (e) => {
    const { value: optionValue, checked } = e.target;

    if (question.type === "open") {
      onChange(question.id, optionValue);
    } else if (question.type === "tel") {
      // add spaces to phone num so it's formatted nicer
      const phoneNumWithSpaces = addSpacesToPhoneNumber(optionValue);
      onChange(question.id, phoneNumWithSpaces);
    } else if (question.type === "close") {
      // Assuming value is always an array for close-ended questions
      onChange(question.id, optionValue);
    } else if (question.type === "choice") {
      onChange(question.id, optionValue);
    } else if (question.type === "file") {
      onChange(question.id, e.target.files[0]);
    }
  };

  const bulbIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="w-6 h-6"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
      />
    </svg>
  );

  return (
    <div
      className="flex flex-col gap-4 md:ml-4 sm:mt-8"
      key={question.id}
    >
      <div className="">
        {question.type === "info" ? (
          <div className="flex align-middle items-center  gap-2 mb-2 text-xl font-light text-blue-800 py-2 border-b-2 px-2 border-blue-600 self-start md:w-2/3">
            <div className="text-blue-700">{bulbIcon}</div>
            <div className="">{question.text}</div>
          </div>
        ) : (
          <h2 className="mb-4 text-2xl text-gray-800">
            {question.text}
          </h2>
        )}
        {question.description && (
          <p className="mb-2 text-md font-l leading-loose text-neutral-500 lg:w-2/3 ">
            {question.description}
          </p>
        )}
        {question.type === "open" && (
          <div className="flex flex-col">
            <div className="flex gap-2">
              <input
                type={question.id === "email" ? "email" : "text"}
                placeholder={question.placeholder || "Type your answer here"}
                value={value || question.defaultValue || ""}
                onChange={handleInputChange}
                maxLength={question.maxLen || 1000}
                autoComplete={
                  question.id === "firstName"
                    ? "given-name"
                    : question.id === "lastName"
                    ? "family-name"
                    : question.id === "email"
                    ? "email"
                    : undefined
                }
                className="flex-1 border p-2 rounded-md placeholder:text-neutral-400 font-extralight border-black w-full outline-none outline-transparent text-lg text-black"
              />
              {isGoogleReviewQuestion && (
                <button
                  type="button"
                  onClick={() => setGoogleReviewFinderOpen(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors flex items-center gap-2 font-medium"
                  title="Don't have a link? Click to find your business"
                >
                  <Search size={20} />
                  <span>Find Link</span>
                </button>
              )}
            </div>
            {question.maxLen && (
              <div className="text-right text-sm text-neutral-500">
                {value?.length || 0}/{question.maxLen}
              </div>
            )}
            {isGoogleReviewQuestion && (
              <GoogleReviewFinderModal
                open={googleReviewFinderOpen}
                setOpen={setGoogleReviewFinderOpen}
                onSelectLink={(link) => onChange(question.id, link)}
                currentValue={value || question.defaultValue || ""}
                businessName={answers.businessName || ""}
              />
            )}
          </div>
        )}
        {question.type === "tel" && (
          <input
            type="tel"
            placeholder={question.placeholder || "123-456-7890"}
            value={value}
            onChange={handleInputChange}
            pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
            autoComplete="tel"
            className="border p-2 rounded-md placeholder:text-neutral-400 font-extralight mt-4 border-black w-full outline-none outline-transparent text-lg text-black"
          />
        )}
        {question.type === "close" && question.options && (
          <select
            value={
              question.options.find((option) => option.text === value)?.text ||
              question.options[0].text
            }
            onChange={handleInputChange}
            className="border p-2 rounded-md placeholder:text-neutral-400 font-extralight mt-2 border-black w-full outline-none outline-transparent text-md text-black"
          >
            {question.options.map((option, index) => (
              <option key={index} value={option.text}>
                {option.text}
              </option>
            ))}
          </select>
        )}
        {question.type === "choice" && question.options && (
          <div className="flex flex-col gap-3 mt-4">
            {question.options.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onChange(question.id, option.value)}
                className={`border-2 rounded-lg p-4 text-left transition-all ${
                  value === option.value
                    ? "border-blue-600 bg-blue-50 text-blue-900"
                    : "border-gray-300 hover:border-gray-400 bg-white text-gray-800"
                }`}
              >
                <div className="font-medium">{option.text}</div>
                {option.description && (
                  <div className="text-sm text-gray-600 mt-1">
                    {option.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        {question.type === "file" && (
          <div className="space-y-6">
            {/* Image Crop Modal */}
            <ImageCropModal
              open={cropModalOpen}
              setOpen={setCropModalOpen}
              imageSrc={tempImageSrc}
              onCropComplete={async (croppedBlob) => {
                setIsUploading(true);
                try {
                  const croppedFile = new File(
                    [croppedBlob],
                    "profile-image.jpg",
                    {
                      type: "image/jpeg",
                    }
                  );
                  const profilePicUrl = await handleFileUpload(croppedFile);
                  onChange(question.id, profilePicUrl);
                } catch (error) {
                  console.error("Error uploading image:", error);
                  alert("Failed to upload image. Please try again.");
                } finally {
                  setIsUploading(false);
                }
              }}
            />

            {/* Preview Area */}
            {answers[question.id] ? (
              <div className="flex flex-col items-center gap-4">
                <div className="relative group">
                  <img
                    className="h-32 w-32 sm:h-40 sm:w-40 object-cover rounded-full shadow-lg border-4 border-white ring-2 ring-gray-200"
                    src={answers[question.id]}
                    alt="Profile"
                  />
                  <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-50 rounded-full cursor-pointer transition-all group-hover:opacity-100 opacity-0">
                    <Camera className="text-white" size={24} />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.addEventListener("load", () => {
                            setTempImageSrc(reader.result);
                            setCropModalOpen(true);
                          });
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>
                <button
                  onClick={() => {
                    document
                      .getElementById(`file-input-${question.id}`)
                      .click();
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Change photo
                </button>
              </div>
            ) : (
              /* Upload Button - Google Drive style */
              <label className="block">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 sm:p-12 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer group">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors">
                      <Upload className="text-blue-600" size={32} />
                    </div>
                    <div className="text-center">
                      <p className="text-base sm:text-lg font-medium text-gray-900">
                        Upload your profile photo
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Click to browse or drag and drop
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        PNG, JPG or WEBP up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
                <input
                  id={`file-input-${question.id}`}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.addEventListener("load", () => {
                        setTempImageSrc(reader.result);
                        setCropModalOpen(true);
                      });
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            )}

            {/* Uploading State */}
            {isUploading && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                Uploading...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Question;
